import { Component, computed, inject, input, OnInit, output, resource, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, lastValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatSuffix } from '@angular/material/form-field';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EcabButtonComponent, EcabSelectComponent } from '@ecab/ecab-ui';
import {
  ApiClient,
  ContactSubjectDto,
  IMyEcabAddressDto,
  IMyEcabBuildingSummaryDto,
  IMyEcabPersonDto,
  MessageWithAttachmentsDto,
  MessageWithAttachmentsDtoQuestionType,
  MyEcabIncidentSummaryDto,
  UpdateContextMessageDto,
  UpdateContextMessageDtoCaseType,
  UpdateContextMessageDtoQuestionType
} from '../../../services/api.clients.generated';
import { IcareService } from '../../../services/icare.service';
import { NotificationService } from '../../../notifications/notification.service';
import {
  formatPersonDisplay,
  getPersonLocality,
  getPersonPrimaryLabel,
  PERSON_SEARCH_DEBOUNCE_MS,
  sortPersonOptions
} from '../../person-picker.utils';

interface SelectOption {
  value: string;
  label: string;
}

/**
 * "Transférer" dialog: edits the qualification context (insured person, building,
 * case, category, subject) of a single message and saves it in one atomic call.
 * Re-homes the editing that previously lived in the right sidebar.
 */
@Component({
  selector: 'app-transfer-dialog',
  imports: [
    DatePipe,
    FormsModule,
    MatFormField,
    MatInput,
    MatSuffix,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatIcon,
    MatProgressSpinnerModule,
    TranslatePipe,
    EcabButtonComponent,
    EcabSelectComponent
  ],
  templateUrl: './transfer-dialog.component.html',
  styleUrl: './transfer-dialog.component.scss'
})
export class TransferDialogComponent implements OnInit {
  private readonly icareService = inject(IcareService);
  private readonly messageService = inject(ApiClient);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);

  readonly message = input.required<MessageWithAttachmentsDto>();
  readonly close = output<void>();
  readonly saved = output<void>();

  protected readonly saving = signal(false);

  // Assuré (person) — debounced ICARE search
  protected readonly personSearchTerm = signal<string | IMyEcabPersonDto | null>(null);
  private readonly personSearch$ = new Subject<string>();
  private readonly debouncedPersonSearchTerm = toSignal(
    this.personSearch$.pipe(debounceTime(PERSON_SEARCH_DEBOUNCE_MS), distinctUntilChanged()),
    { initialValue: '' }
  );
  protected readonly selectedPerson = signal<IMyEcabPersonDto | null>(null);
  protected readonly personsResource = resource<IMyEcabPersonDto[], string>({
    params: () => {
      const value = this.debouncedPersonSearchTerm();
      return typeof value === 'string' ? value : '';
    },
    loader: async ({ params }) => {
      if (!params) {
        return [];
      }
      const response = await lastValueFrom(this.icareService.getPersons(params));
      return response.content ?? [];
    }
  });
  protected readonly personsOptions = computed(() => sortPersonOptions(this.personsResource.value() ?? []));

  // Bâtiment (building) — depends on the selected person
  protected readonly selectedBuildingId = signal('');
  private readonly buildingAddress = signal('');
  protected readonly buildingsResource = resource<IMyEcabBuildingSummaryDto[], string | undefined>({
    params: () => this.selectedPerson()?.id?.toString() || undefined,
    loader: async ({ params }) => {
      if (!params) {
        return [];
      }
      const response = await lastValueFrom(this.icareService.getBuildings(params));
      return response.content ?? [];
    }
  });
  protected readonly buildingOptions = computed<SelectOption[]>(() => [
    { value: '', label: this.translateService.instant('message.context.noAddress') },
    ...(this.buildingsResource.value() ?? []).map(building => ({
      value: building.id?.toString() ?? '',
      label: this.buildingLabel(building)
    }))
  ]);

  // Dossier (case) — depends on person + building
  protected readonly selectedCaseId = signal('');
  private readonly caseTitle = signal('');
  private readonly caseType = signal<string>('NONE');
  protected readonly casesResource = resource<MyEcabIncidentSummaryDto[], string | undefined>({
    params: () => {
      const personId = this.selectedPerson()?.id?.toString();
      const buildingId = this.selectedBuildingId();
      return personId && buildingId ? `${personId}|${buildingId}` : undefined;
    },
    loader: async ({ params }) => {
      if (!params) {
        return [];
      }
      const [personId, buildingId] = params.split('|');
      const response = await lastValueFrom(this.icareService.getCases(personId, buildingId));
      return response.content ?? [];
    }
  });
  protected readonly caseOptions = computed<SelectOption[]>(() => [
    { value: '', label: this.translateService.instant('message.context.noCase') },
    ...(this.casesResource.value() ?? []).map(incident => ({
      value: incident.id?.toString() ?? '',
      label: this.formatIncidentLabel(incident)
    }))
  ]);

  // Catégorie (question type) + Sujet
  protected readonly questionType = signal<string>('');
  protected readonly subject = signal('');
  protected readonly subjectsResource = resource<ContactSubjectDto[], undefined>({
    loader: async () => await lastValueFrom(this.messageService.getContactSubjects())
  });
  protected readonly subjectOptions = computed<SelectOption[]>(() => {
    const subjects = this.subjectsResource.value() ?? [];
    const options = subjects.map(subject => ({
      value: subject.name,
      label: this.translateService.instant('message.contactSubject.' + subject.name)
    }));

    // Keep the current subject selectable even before the list has loaded, or when
    // it's a legacy free-text value. Translate it (the stored value is the enum name,
    // and the i18n is keyed by name), falling back to the raw value when unknown.
    const current = this.subject();
    if (current && !subjects.some(subject => subject.name === current)) {
      const path = 'message.contactSubject.' + current;
      const label = this.translateService.instant(path);
      options.unshift({ value: current, label: label === path ? current : label });
    }
    return options;
  });
  protected readonly questionTypeOptions = computed<SelectOption[]>(() => [
    { value: '', label: this.translateService.instant('message.context.noQuestionType') },
    { value: MessageWithAttachmentsDtoQuestionType.MY_ACCOUNT, label: this.translateService.instant('message.questionType.ACCOUNT') },
    { value: MessageWithAttachmentsDtoQuestionType.GENERAL_QUESTION, label: this.translateService.instant('message.questionType.GENERAL') },
    { value: MessageWithAttachmentsDtoQuestionType.CLAIM, label: this.translateService.instant('message.questionType.CLAIM') },
    { value: MessageWithAttachmentsDtoQuestionType.PREVENTION, label: this.translateService.instant('message.questionType.PREVENTION') },
    { value: MessageWithAttachmentsDtoQuestionType.ESTIMATION, label: this.translateService.instant('message.questionType.ESTIMATION') },
    { value: MessageWithAttachmentsDtoQuestionType.BUILDINGS, label: this.translateService.instant('message.questionType.BUILDING') },
    { value: MessageWithAttachmentsDtoQuestionType.SUBSIDIES, label: this.translateService.instant('message.questionType.SUBSIDY') }
  ]);

  protected readonly canSave = computed(() => !this.saving() && this.subject().trim().length > 0);

  ngOnInit(): void {
    const message = this.message();
    this.questionType.set(message.questionType ?? '');
    this.subject.set(message.subject ?? '');
    this.selectedBuildingId.set(message.buildingId ?? '');
    this.buildingAddress.set(message.buildingAddress ?? '');
    this.selectedCaseId.set(message.caseId ?? '');
    this.caseTitle.set(message.caseTitle ?? '');
    this.caseType.set(message.caseType ?? 'NONE');

    if (message.personId) {
      const skeleton = {
        id: Number(message.personId),
        firstName: message.firstname,
        lastName: message.lastname,
        companyName: message.companyName
      } as unknown as IMyEcabPersonDto;
      this.selectedPerson.set(skeleton);
      this.personSearchTerm.set(skeleton);
    }
  }

  displayPerson(person: IMyEcabPersonDto | string | null): string {
    return formatPersonDisplay(person);
  }

  getPersonPrimaryLabel(person: IMyEcabPersonDto): string {
    return getPersonPrimaryLabel(person);
  }

  getPersonLocality(person: IMyEcabPersonDto): string {
    return getPersonLocality(person);
  }

  protected onPersonSearchChange(value: string | IMyEcabPersonDto | null): void {
    if (typeof value === 'string') {
      this.personSearchTerm.set(value);
      this.personSearch$.next(value);
    } else if (value && typeof value === 'object') {
      this.onPersonSelected(value);
    } else {
      this.clearPerson();
    }
  }

  protected onPersonSelected(person: IMyEcabPersonDto): void {
    this.selectedPerson.set(person);
    this.personSearchTerm.set(person);
    // A new person invalidates the dependent building + case selection.
    this.clearBuilding();
  }

  protected clearPerson(): void {
    this.selectedPerson.set(null);
    this.personSearchTerm.set(null);
    this.clearBuilding();
  }

  private clearBuilding(): void {
    this.selectedBuildingId.set('');
    this.buildingAddress.set('');
    this.clearCase();
  }

  private clearCase(): void {
    this.selectedCaseId.set('');
    this.caseTitle.set('');
    this.caseType.set('NONE');
  }

  protected onBuildingChange(value: string | null): void {
    this.selectedBuildingId.set(value ?? '');
    const summary = (this.buildingsResource.value() ?? []).find(building => building.id?.toString() === value);
    this.buildingAddress.set(summary?.address ? this.formatAddress(summary.address) : '');
    this.clearCase();
  }

  protected onCaseChange(value: string | null): void {
    this.selectedCaseId.set(value ?? '');
    const incident = (this.casesResource.value() ?? []).find(item => item.id?.toString() === value);
    this.caseTitle.set(incident ? this.formatIncidentLabel(incident) : '');
    this.caseType.set(value ? 'DAMAGE' : 'NONE');
  }

  protected onQuestionTypeChange(value: string | null): void {
    this.questionType.set(value ?? '');
  }

  protected onSubjectChange(value: string | null): void {
    this.subject.set(value ?? '');
  }

  protected cancel(): void {
    this.close.emit();
  }

  protected async save(): Promise<void> {
    const message = this.message();
    if (!message.id || !this.canSave()) {
      return;
    }

    this.saving.set(true);
    const person = this.selectedPerson();
    const body = new UpdateContextMessageDto();
    body.personId = person?.id?.toString() ?? '';
    body.companyName = person?.companyName ?? '';
    body.firstname = (person as { firstName?: string } | null)?.firstName ?? '';
    body.lastname = (person as { lastName?: string } | null)?.lastName ?? '';
    body.buildingId = this.selectedBuildingId();
    body.buildingSource = this.selectedBuildingId() ? 'ICARE' : '';
    body.buildingAddress = this.buildingAddress();
    body.caseId = this.selectedCaseId();
    body.caseSource = this.selectedCaseId() ? 'ICARE' : '';
    body.caseType = this.caseType() as UpdateContextMessageDtoCaseType;
    body.caseTitle = this.caseTitle();
    body.questionType = (this.questionType() || null) as UpdateContextMessageDtoQuestionType;
    body.subject = this.subject().trim();

    try {
      await lastValueFrom(this.messageService.updateInternalMessageContext(message.id, body));
      this.notificationService.success(this.translateService.instant('message.details.feedback.contextUpdated'));
      this.saved.emit();
    } catch (error) {
      this.notificationService.error(
        this.notificationService.errorMessage(error, this.translateService.instant('message.details.feedback.contextUpdateError'))
      );
    } finally {
      this.saving.set(false);
    }
  }

  private buildingLabel(building: IMyEcabBuildingSummaryDto): string {
    if (!building.address) {
      return building.egidNumber?.toString() ?? '';
    }
    const address = building.address;
    return `${building.egidNumber ?? ''} - ${address.street?.name ?? ''} ${address.streetNumber ?? ''}${address.streetNumberSuffix ?? ''}, ${address.street?.locality?.zipCode ?? ''} ${address.street?.locality?.name ?? ''}`.trim();
  }

  private formatAddress(address: IMyEcabAddressDto): string {
    if (!address) {
      return '';
    }
    const streetName = address.street?.name ?? '';
    const localityZipCode = address.street?.locality?.zipCode ?? '';
    const localityName = address.street?.locality?.name ?? '';
    const num = `${address.streetNumber}${address.streetNumberSuffix ?? ''}`;
    return `${streetName} ${num}, ${localityZipCode} ${localityName}`.trim();
  }

  private formatIncidentLabel(incident: MyEcabIncidentSummaryDto | null | undefined): string {
    if (!incident) {
      return '';
    }
    return [incident.incidentNumberFull, incident.eventType]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .join(' ');
  }
}
